<!-- src/views/Restaurante/MenuDetalle.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- BREADCRUMB + HEADER -->
    <div class="mb-6 animate-fadeIn">
      <button @click="$router.push({ name: 'MenusList' })"
        class="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-black text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
        <Icon icon="mdi:arrow-left" class="text-base" /> Volver a Menús
      </button>
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <div class="w-10 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
            <span class="text-[10px] font-black text-purple-600 dark:text-purple-400 tracking-widest uppercase">Detalle del Menú</span>
          </div>
          <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
            {{ menu?.nombre || 'Cargando...' }}
            <span v-if="menu?.es_principal"
              class="px-2 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow">
              <Icon icon="mdi:star" class="inline text-xs" /> Principal
            </span>
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">{{ menu?.descripcion || 'Sin descripción' }}</p>
        </div>
        <button @click="openCreateCategoria"
          class="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs sm:text-sm shadow-lg flex items-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" />
          Nueva Categoría
        </button>
      </div>
    </div>

    <!-- ESTADÍSTICAS -->
    <div class="grid grid-cols-3 gap-3 sm:gap-5 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Categorías</p>
        <p class="text-3xl font-black text-purple-600">{{ categorias.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Productos</p>
        <p class="text-3xl font-black text-blue-600">{{ totalProductos }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Destacados</p>
        <p class="text-3xl font-black text-amber-500">{{ totalDestacados }}</p>
      </div>
    </div>

    <!-- LAYOUT 2 COLUMNAS -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">

      <!-- CATEGORIAS -->
      <div class="lg:col-span-4 bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeInUp">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:folder-multiple" class="text-purple-600 text-xl" />
            <h3 class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">Categorías</h3>
          </div>
          <span class="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-lg text-[10px] font-black">{{ categorias.length }}</span>
        </div>
        <div class="p-3 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-2">
          <div v-if="loadingCategorias" class="text-center py-10 text-gray-400">
            <Icon icon="mdi:loading" class="animate-spin text-3xl" />
          </div>
          <div v-else-if="categorias.length === 0" class="text-center py-10 opacity-50">
            <Icon icon="mdi:folder-off" class="text-5xl mb-2 text-gray-300" />
            <p class="text-xs font-bold text-gray-400">Sin categorías</p>
          </div>
          <button v-else v-for="cat in categorias" :key="cat.idMenuCategoria"
            @click="seleccionarCategoria(cat)"
            :class="['w-full text-left p-3 rounded-xl border-2 transition-all group',
              categoriaActiva?.idMenuCategoria === cat.idMenuCategoria
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 border-transparent text-white shadow-lg'
                : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300']">
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <Icon :icon="cat.icono || 'mdi:folder'" class="text-base shrink-0" />
                <div class="min-w-0">
                  <div class="text-sm font-black truncate">{{ cat.nombre }}</div>
                  <div :class="['text-[10px] font-bold', categoriaActiva?.idMenuCategoria === cat.idMenuCategoria ? 'text-white/70' : 'text-gray-400']">
                    Orden #{{ cat.orden }}
                  </div>
                </div>
              </div>
              <div class="flex gap-1 shrink-0">
                <button @click.stop="openEditCategoria(cat)"
                  :class="['p-1 rounded transition-all', categoriaActiva?.idMenuCategoria === cat.idMenuCategoria ? 'hover:bg-white/20 text-white' : 'hover:bg-amber-50 text-amber-500']">
                  <Icon icon="mdi:pencil" class="text-sm" />
                </button>
                <button @click.stop="abrirModalEliminarCategoria(cat)"
                  :class="['p-1 rounded transition-all', categoriaActiva?.idMenuCategoria === cat.idMenuCategoria ? 'hover:bg-white/20 text-white' : 'hover:bg-rose-50 text-rose-500']">
                  <Icon icon="mdi:delete" class="text-sm" />
                </button>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- PRODUCTOS -->
      <div class="lg:col-span-8 bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:food" class="text-blue-600 text-xl" />
            <h3 class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">
              Productos
              <span v-if="categoriaActiva" class="text-purple-600">· {{ categoriaActiva.nombre }}</span>
            </h3>
          </div>
          <button v-if="categoriaActiva" @click="openCreateProducto"
            class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-black text-[10px] uppercase tracking-widest flex items-center gap-1">
            <Icon icon="mdi:plus" class="text-sm" /> Agregar
          </button>
        </div>

        <div class="p-4">
          <div v-if="!categoriaActiva" class="text-center py-16 opacity-50">
            <Icon icon="mdi:cursor-default-click" class="text-6xl mb-3 text-gray-300" />
            <p class="text-sm font-bold text-gray-400">Selecciona una categoría a la izquierda</p>
          </div>

          <div v-else-if="loadingProductos" class="text-center py-10">
            <Icon icon="mdi:loading" class="animate-spin text-3xl text-gray-400" />
          </div>

          <div v-else-if="productos.length === 0" class="text-center py-10 opacity-50">
            <Icon icon="mdi:food-off" class="text-5xl mb-2 text-gray-300" />
            <p class="text-xs font-bold text-gray-400 mb-3">Sin productos en esta categoría</p>
            <button @click="openCreateProducto"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg font-black text-[10px] uppercase">
              + Agregar primero
            </button>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div v-for="prod in productos" :key="prod.idMenuProducto"
              class="bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-700 p-3 relative overflow-hidden group hover:shadow-md transition-all">
              <div class="absolute top-2 right-2 flex gap-1">
                <button @click.stop="openEditProducto(prod)"
                  class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-500 rounded-lg hover:bg-amber-50">
                  <Icon icon="mdi:pencil" class="text-sm" />
                </button>
                <button @click.stop="abrirModalEliminarProducto(prod)"
                  class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50">
                  <Icon icon="mdi:delete" class="text-sm" />
                </button>
              </div>

              <div class="flex items-start gap-2 mb-2 pr-16">
                <Icon icon="mdi:food" class="text-blue-600 text-lg shrink-0 mt-0.5" />
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <h4 class="text-sm font-black text-gray-900 dark:text-white truncate">{{ prod.nombreProducto }}</h4>
                    <Icon v-if="prod.es_destacado" icon="mdi:star" class="text-amber-400 text-xs" />
                  </div>
                  <p class="text-[10px] font-bold text-gray-400 truncate">{{ prod.descripcion_corta || 'Sin descripción' }}</p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-1.5 text-[10px] mt-2">
                <div v-if="prod.precio_especial_usd != null" class="bg-white dark:bg-gray-800 rounded-lg px-2 py-1 border border-gray-200 dark:border-gray-600">
                  <span class="font-bold text-gray-400">USD</span>
                  <span class="font-black text-emerald-600 ml-1">${{ formatearMonto(prod.precio_especial_usd, 2) }}</span>
                </div>
                <div v-if="prod.precio_especial_bs != null" class="bg-white dark:bg-gray-800 rounded-lg px-2 py-1 border border-gray-200 dark:border-gray-600">
                  <span class="font-bold text-gray-400">Bs</span>
                  <span class="font-black text-blue-600 ml-1">{{ formatearMonto(prod.precio_especial_bs, 2) }}</span>
                </div>
              </div>

              <div class="mt-2 flex items-center justify-between">
                <button @click.stop="toggleDisponibilidad(prod)"
                  :class="['px-2 py-1 rounded-lg font-black text-[9px] uppercase tracking-widest border',
                    prod.disponible
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border-rose-200']">
                  <Icon :icon="prod.disponible ? 'mdi:check-circle' : 'mdi:close-circle'" class="inline text-xs mr-0.5" />
                  {{ prod.disponible ? 'Disponible' : 'No disp.' }}
                </button>
                <span v-if="prod.nombreArea" class="text-[9px] font-bold text-gray-400 flex items-center gap-0.5">
                  <Icon icon="mdi:stove" class="text-xs" /> {{ prod.nombreArea }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CATEGORIA -->
    <Teleport to="body">
      <div v-if="showCategoriaModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeCategoriaModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingCategoria ? 'mdi:pencil-box' : 'mdi:folder-plus'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingCategoria ? 'Editar Categoría' : 'Nueva Categoría' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingCategoria ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeCategoriaModal" class="ml-auto p-2 bg-white/10 rounded-xl">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveCategoria" class="space-y-4">
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Nombre *</label>
                <input v-model="formCat.nombre" required maxlength="100"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none"
                  placeholder="Ej: Entradas, Platos Fuertes" />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Ícono</label>
                  <input v-model="formCat.icono" maxlength="50"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none"
                    placeholder="mdi:food" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Orden</label>
                  <input v-model.number="formCat.orden" type="number" min="0"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none" />
                </div>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Descripción</label>
                <textarea v-model="formCat.descripcion" rows="2" maxlength="200"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none resize-none"></textarea>
              </div>
              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeCategoriaModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Cancelar
                </button>
                <button type="submit" :disabled="savingCategoria"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
                  <Icon :icon="savingCategoria ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': savingCategoria}" class="text-lg" />
                  {{ savingCategoria ? 'Guardando...' : (editingCategoria ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL PRODUCTO -->
    <Teleport to="body">
      <div v-if="showProductoModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeProductoModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingProducto ? 'mdi:pencil-box' : 'mdi:food'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingProducto ? 'Editar Producto' : 'Agregar Producto' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingProducto ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeProductoModal" class="ml-auto p-2 bg-white/10 rounded-xl">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveProducto" class="space-y-4">

              <!-- BUSCADOR DE PRODUCTO -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">
                  Producto del Inventario *
                </label>

                <div v-if="productoSeleccionado"
                  class="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800/50">
                  <div class="w-10 h-10 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center border border-blue-200 dark:border-blue-800/50 shrink-0">
                    <Icon icon="mdi:package-variant" class="text-blue-600 text-xl" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-black text-gray-900 dark:text-white truncate">{{ productoSeleccionado.nombre }}</p>
                    <p class="text-[10px] font-bold text-gray-500 mt-0.5">
                      <span v-if="productoSeleccionado.codigo">Cód: {{ productoSeleccionado.codigo }} · </span>
                      <span v-if="productoSeleccionado.codigoBarras">CB: {{ productoSeleccionado.codigoBarras }} · </span>
                      ID: {{ productoSeleccionado.idProducto }}
                      <span v-if="productoSeleccionado.stock != null" class="text-emerald-600"> · Stock: {{ productoSeleccionado.stock }}</span>
                    </p>
                    <p v-if="productoSeleccionado.precio_venta_usd" class="text-[10px] font-bold text-emerald-600 mt-0.5">
                      Venta: ${{ formatearMonto(productoSeleccionado.precio_venta_usd, 2) }}
                      <span v-if="productoSeleccionado.precio_venta_bs"> · Bs {{ formatearMonto(productoSeleccionado.precio_venta_bs, 2) }}</span>
                    </p>
                  </div>
                  <button type="button" @click="limpiarProductoSeleccionado"
                    class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50 transition-all shrink-0">
                    <Icon icon="mdi:close" class="text-base" />
                  </button>
                </div>

                <div v-else class="relative">
                  <div class="relative">
                    <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    <input
                      v-model="productoBusqueda"
                      @focus="onFocusBuscadorProducto"
                      @blur="onBlurBuscadorProducto"
                      type="text"
                      placeholder="Buscar por nombre, código, código de barras..."
                      class="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" />
                  </div>

                  <div v-if="mostrarDropdownProductos"
                    class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800/50 rounded-xl shadow-2xl max-h-72 overflow-y-auto custom-scrollbar z-50">

                    <div v-if="cargandoInventario" class="p-6 text-center">
                      <Icon icon="mdi:loading" class="animate-spin text-2xl text-blue-500 mb-2" />
                      <p class="text-xs text-gray-400 font-bold">Cargando inventario...</p>
                    </div>

                    <div v-else-if="productosInventario.length === 0" class="p-6 text-center">
                      <Icon icon="mdi:package-variant-remove" class="text-3xl text-gray-300 mb-2" />
                      <p class="text-xs text-gray-400 font-bold">No hay productos en el inventario</p>
                    </div>

                    <div v-else-if="productosFiltradosDropdown.length === 0" class="p-6 text-center">
                      <Icon icon="mdi:magnify-close" class="text-3xl text-gray-300 mb-2" />
                      <p class="text-xs text-gray-400 font-bold">No se encontraron productos</p>
                    </div>

                    <button v-else v-for="p in productosFiltradosDropdown" :key="p.idProducto"
                      type="button"
                      @mousedown.prevent="seleccionarProductoInventario(p)"
                      class="w-full text-left p-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors flex items-center gap-3">
                      <div class="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                        <Icon icon="mdi:package-variant" class="text-blue-600 text-lg" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-black text-gray-900 dark:text-white truncate">{{ p.nombre }}</p>
                        <p class="text-[10px] font-bold text-gray-400 truncate">
                          <span v-if="p.codigo">{{ p.codigo }}</span>
                          <span v-if="p.codigoBarras"> · {{ p.codigoBarras }}</span>
                          <span v-if="p.nombreCategoria"> · {{ p.nombreCategoria }}</span>
                        </p>
                      </div>
                      <div class="text-right shrink-0">
                        <p v-if="p.precio_venta_usd" class="text-[11px] font-black text-emerald-600">
                          ${{ formatearMonto(p.precio_venta_usd, 2) }}
                        </p>
                        <p v-if="p.stock != null"
                          :class="['text-[10px] font-bold',
                            p.stock <= 0 ? 'text-rose-500' :
                            (p.stock_minimo && p.stock <= p.stock_minimo) ? 'text-amber-500' : 'text-gray-400']">
                          Stock: {{ p.stock }}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

                <p class="text-[9px] font-medium text-gray-400 mt-2 flex items-center gap-1">
                  <Icon icon="mdi:information-outline" class="text-xs" />
                  Los precios se autocompletan desde el inventario. Puedes ajustarlos si necesitas precio especial.
                </p>
              </div>

              <!-- PRECIOS CON FORMATO VENEZOLANO -->
              <div class="grid grid-cols-2 gap-3">

                <div class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-4 border-2 border-emerald-200 dark:border-emerald-800/50">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <Icon icon="mdi:currency-usd" class="text-white text-sm" />
                    </div>
                    <label class="text-[10px] font-black text-emerald-700 dark:text-emerald-300 uppercase tracking-widest">
                      Precio USD
                    </label>
                  </div>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-base">$</span>
                    <input
                      :value="formUSDDisplay"
                      @input="onInputPrecioUSD"
                      type="text"
                      inputmode="decimal"
                      placeholder="0,00"
                      class="w-full pl-8 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-emerald-200 dark:border-emerald-800/50 focus:border-emerald-500 rounded-xl text-base font-black text-emerald-700 dark:text-emerald-300 outline-none transition-all text-right tabular-nums" />
                  </div>
                  <p class="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold mt-1.5 flex items-center gap-1">
                    <Icon icon="mdi:information-outline" class="text-xs" />
                    Formato: 33.655,00
                  </p>
                </div>

                <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-4 border-2 border-blue-200 dark:border-blue-800/50">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-6 h-6 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Icon icon="mdi:currency-ven" class="text-white text-sm" />
                    </div>
                    <label class="text-[10px] font-black text-blue-700 dark:text-blue-300 uppercase tracking-widest">
                      Precio Bs
                    </label>
                  </div>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 font-black text-xs">Bs</span>
                    <input
                      :value="formBsDisplay"
                      @input="onInputPrecioBs"
                      type="text"
                      inputmode="decimal"
                      placeholder="0,00"
                      class="w-full pl-9 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800/50 focus:border-blue-500 rounded-xl text-base font-black text-blue-700 dark:text-blue-300 outline-none transition-all text-right tabular-nums" />
                  </div>
                  <p class="text-[9px] text-blue-600 dark:text-blue-400 font-bold mt-1.5 flex items-center gap-1">
                    <Icon icon="mdi:information-outline" class="text-xs" />
                    Formato: 336.550,00
                  </p>
                </div>
              </div>

              <!-- AREA COCINA -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Área de Cocina (opcional)</label>
                <select v-model.number="formProd.idAreaCocina"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none">
                  <option :value="null">Sin área específica</option>
                  <option v-for="a in areasCocina" :key="a.idAreaCocina" :value="a.idAreaCocina">{{ a.nombre }}</option>
                </select>
              </div>

              <!-- ORDEN + DESTACADO -->
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Orden</label>
                  <input v-model.number="formProd.orden" type="number" min="0"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Destacado</label>
                  <label class="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" v-model="formProd.es_destacado" class="w-5 h-5 rounded text-amber-500" />
                    <span class="text-xs font-black text-gray-700 dark:text-gray-300">⭐ Destacar</span>
                  </label>
                </div>
              </div>

              <!-- DESCRIPCION CORTA -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Descripción Corta</label>
                <textarea v-model="formProd.descripcion_corta" rows="2" maxlength="200"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none resize-none"></textarea>
              </div>

              <!-- DISPONIBLE -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formProd.disponible" class="w-5 h-5 rounded text-emerald-600" />
                  <div>
                    <span class="text-sm font-black text-gray-700 dark:text-gray-200">Disponible</span>
                    <p class="text-[10px] text-gray-400 font-medium">Se puede pedir ahora mismo</p>
                  </div>
                </label>
              </div>

              <!-- BOTONES -->
              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeProductoModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Cancelar
                </button>
                <button type="submit" :disabled="savingProducto"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
                  <Icon :icon="savingProducto ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': savingProducto}" class="text-lg" />
                  {{ savingProducto ? 'Guardando...' : (editingProducto ? 'Actualizar' : 'Agregar') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-100">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import {
  obtenerMenuPorId, obtenerCategoriasPorMenu, crearCategoria, actualizarCategoria, eliminarCategoria,
  obtenerProductosPorCategoria, crearProducto, actualizarProducto, eliminarProducto, cambiarDisponibilidadProducto,
} from '@/api/menus.js';
import { obtenerAreasActivas } from '@/api/areasCocina.js';
import { obtenerProductos, buscarProductosLocal, formatearMonto } from '@/api/productos.js';

export default {
  name: 'MenuDetalle',
  components: { Icon },
  data() {
    return {
      menu: null,
      categorias: [], categoriaActiva: null,
      productos: [],
      areasCocina: [],

      // Inventario para el buscador
      productosInventario: [],
      cargandoInventario: false,
      productoBusqueda: '',
      productoSeleccionado: null,
      mostrarDropdownProductos: false,

      loadingCategorias: false, loadingProductos: false,

      // Categoría form
      showCategoriaModal: false, savingCategoria: false, editingCategoria: null,
      formCat: { nombre: '', descripcion: '', icono: '', orden: 0, estado: 'activo' },

      // Producto form
      showProductoModal: false, savingProducto: false, editingProducto: null,
      formProd: {
        idProducto: null, idAreaCocina: null,
        precio_especial_usd: null, precio_especial_bs: null,
        disponible: true, es_destacado: false,
        orden: 0, descripcion_corta: '',
      },
      // Displays formateados
      formUSDDisplay: '',
      formBsDisplay: '',

      // Confirmación
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    totalProductos() { return this.productos.length; },
    totalDestacados() { return this.productos.filter(p => p.es_destacado).length; },

    productosFiltradosDropdown() {
      if (!this.productoBusqueda.trim()) {
        return this.productosInventario.slice(0, 10);
      }
      return buscarProductosLocal(this.productosInventario, this.productoBusqueda).slice(0, 15);
    },
  },
  async mounted() {
    await this.cargarMenu();
    await this.cargarCategorias();
    await this.cargarAreas();
    await this.cargarInventario();
  },
  methods: {
    mostrarToast(icon, title, text = '') {
      Swal.fire({ toast: true, position: 'top-end', icon, title, text, showConfirmButton: false, timer: 3000, timerProgressBar: true });
    },

    // ===== FORMATEO DE MONTOS =====
    formatearInputMonto(valor) {
      if (valor === '' || valor === null || valor === undefined) return '';
      let str = String(valor).replace(/[^\d,]/g, '');
      if (!str.includes(',')) str = str + ',';
      let [entero, decimales = ''] = str.split(',');
      entero = entero.replace(/^0+(?=\d)/, '');
      const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      decimales = decimales.substring(0, 2);
      return decimales.length > 0 || str.includes(',')
        ? `${enteroFormateado},${decimales}`
        : enteroFormateado;
    },
    parsearMontoInput(str) {
      if (!str && str !== 0) return null;
      const limpio = String(str).replace(/\./g, '').replace(',', '.');
      const num = parseFloat(limpio);
      return isNaN(num) ? null : num;
    },
    onInputPrecioUSD(e) {
      const formatted = this.formatearInputMonto(e.target.value);
      this.formUSDDisplay = formatted;
      this.formProd.precio_especial_usd = this.parsearMontoInput(formatted);
    },
    onInputPrecioBs(e) {
      const formatted = this.formatearInputMonto(e.target.value);
      this.formBsDisplay = formatted;
      this.formProd.precio_especial_bs = this.parsearMontoInput(formatted);
    },

    async cargarMenu() {
      const id = Number(this.$route.params.id);
      if (!id) return;
      const r = await obtenerMenuPorId(id);
      if (r.success) this.menu = r.data;
    },

    async cargarCategorias() {
      this.loadingCategorias = true;
      try {
        const id = Number(this.$route.params.id);
        const r = await obtenerCategoriasPorMenu(id);
        if (r.success) {
          this.categorias = (r.data || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
          if (this.categorias.length > 0 && !this.categoriaActiva) {
            this.seleccionarCategoria(this.categorias[0]);
          }
        }
      } finally { this.loadingCategorias = false; }
    },

    async cargarAreas() {
      const r = await obtenerAreasActivas();
      if (r.success) this.areasCocina = r.data || [];
    },

    async cargarInventario() {
      this.cargandoInventario = true;
      try {
        const r = await obtenerProductos('activos');
        if (r.success) this.productosInventario = r.data || [];
      } finally {
        this.cargandoInventario = false;
      }
    },

    async seleccionarCategoria(cat) {
      this.categoriaActiva = cat;
      this.loadingProductos = true;
      try {
        const r = await obtenerProductosPorCategoria(cat.idMenuCategoria);
        if (r.success) {
          this.productos = (r.data || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        }
      } finally { this.loadingProductos = false; }
    },

    // ===== BUSCADOR =====
    onFocusBuscadorProducto() { this.mostrarDropdownProductos = true; },
    onBlurBuscadorProducto() {
      setTimeout(() => { this.mostrarDropdownProductos = false; }, 200);
    },
    seleccionarProductoInventario(prod) {
      this.productoSeleccionado = prod;
      this.productoBusqueda = prod.nombre;
      this.formProd.idProducto = prod.idProducto;

      const usd = prod.precio_venta_usd ?? prod.precio_especial_usd ?? null;
      const bs = prod.precio_venta_bs ?? prod.precio_especial_bs ?? null;
      if (usd != null) {
        this.formProd.precio_especial_usd = Number(usd);
        this.formUSDDisplay = formatearMonto(Number(usd), 2);
      }
      if (bs != null) {
        this.formProd.precio_especial_bs = Number(bs);
        this.formBsDisplay = formatearMonto(Number(bs), 2);
      }

      if (!this.formProd.descripcion_corta && prod.descripcion) {
        this.formProd.descripcion_corta = prod.descripcion.substring(0, 200);
      }

      this.mostrarDropdownProductos = false;
    },
    limpiarProductoSeleccionado() {
      this.productoSeleccionado = null;
      this.productoBusqueda = '';
      this.formProd.idProducto = null;
    },

    // ===== CATEGORIA CRUD =====
    openCreateCategoria() {
      this.editingCategoria = null;
      const maxOrden = this.categorias.reduce((m, c) => Math.max(m, c.orden ?? 0), 0);
      this.formCat = { nombre: '', descripcion: '', icono: '', orden: maxOrden + 1, estado: 'activo' };
      this.showCategoriaModal = true;
    },
    openEditCategoria(c) {
      this.editingCategoria = c;
      this.formCat = {
        nombre: c.nombre, descripcion: c.descripcion || '',
        icono: c.icono || '', orden: c.orden ?? 0, estado: c.estado,
      };
      this.showCategoriaModal = true;
    },
    closeCategoriaModal() { this.showCategoriaModal = false; },
    async saveCategoria() {
      this.savingCategoria = true;
      try {
        const payload = { ...this.formCat, idMenu: Number(this.$route.params.id) };
        const r = this.editingCategoria
          ? await actualizarCategoria(this.editingCategoria.idMenuCategoria, payload)
          : await crearCategoria(payload);
        if (r.success) {
          await this.cargarCategorias();
          this.closeCategoriaModal();
          this.mostrarToast('success', '¡Éxito!', `Categoría ${this.editingCategoria ? 'actualizada' : 'creada'}.`);
        } else {
          this.mostrarToast('error', 'Error', r.error || 'Error al guardar');
        }
      } finally { this.savingCategoria = false; }
    },
    abrirModalEliminarCategoria(cat) {
      this.abrirModalConfirmacion({
        titulo: 'Desactivar Categoría', subtitulo: 'Cambiará a inactivo',
        mensaje: `¿Desactivar "${cat.nombre}"?`,
        detalle: 'Los productos dentro de la categoría no se borran.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600',
        botonTexto: 'Desactivar', datos: cat.idMenuCategoria,
        accion: (id) => this.deleteCategoria(id),
      });
    },
    async deleteCategoria(id) {
      const r = await eliminarCategoria(id);
      if (r.success) {
        await this.cargarCategorias();
        if (this.categoriaActiva?.idMenuCategoria === id) {
          this.categoriaActiva = this.categorias[0] || null;
          if (this.categoriaActiva) await this.seleccionarCategoria(this.categoriaActiva);
          else this.productos = [];
        }
        this.mostrarToast('success', 'Desactivada', 'Categoría desactivada.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'No se pudo desactivar.');
      }
    },

    // ===== PRODUCTO CRUD =====
    openCreateProducto() {
      if (!this.categoriaActiva) return;
      this.editingProducto = null;
      this.productoBusqueda = '';
      this.productoSeleccionado = null;
      this.mostrarDropdownProductos = false;
      this.formUSDDisplay = '';
      this.formBsDisplay = '';
      const maxOrden = this.productos.reduce((m, p) => Math.max(m, p.orden ?? 0), 0);
      this.formProd = {
        idProducto: null, idAreaCocina: null,
        precio_especial_usd: null, precio_especial_bs: null,
        disponible: true, es_destacado: false,
        orden: maxOrden + 1, descripcion_corta: '',
      };
      this.showProductoModal = true;
    },
    openEditProducto(p) {
      this.editingProducto = p;
      const inv = this.productosInventario.find(x => x.idProducto === p.idProducto);
      this.productoSeleccionado = inv || {
        idProducto: p.idProducto,
        nombre: p.nombreProducto || `Producto #${p.idProducto}`,
        codigo: '',
        codigoBarras: '',
        stock: null,
        precio_venta_usd: p.precio_especial_usd ?? 0,
        precio_venta_bs: p.precio_especial_bs ?? 0,
      };
      this.productoBusqueda = this.productoSeleccionado.nombre;
      this.mostrarDropdownProductos = false;
      this.formProd = {
        idProducto: p.idProducto, idAreaCocina: p.idAreaCocina,
        precio_especial_usd: p.precio_especial_usd ?? null,
        precio_especial_bs: p.precio_especial_bs ?? null,
        disponible: !!p.disponible, es_destacado: !!p.es_destacado,
        orden: p.orden ?? 0, descripcion_corta: p.descripcion_corta || '',
      };
      this.formUSDDisplay = p.precio_especial_usd != null
        ? formatearMonto(p.precio_especial_usd, 2)
        : '';
      this.formBsDisplay = p.precio_especial_bs != null
        ? formatearMonto(p.precio_especial_bs, 2)
        : '';
      this.showProductoModal = true;
    },
    closeProductoModal() { this.showProductoModal = false; },
    async saveProducto() {
      if (!this.formProd.idProducto) {
        this.mostrarToast('warning', 'Falta producto', 'Selecciona un producto del inventario.');
        return;
      }
      this.savingProducto = true;
      try {
        const payload = {
          ...this.formProd,
          precio_especial_usd: this.parsearMontoInput(this.formUSDDisplay),
          precio_especial_bs: this.parsearMontoInput(this.formBsDisplay),
          idMenuCategoria: this.categoriaActiva.idMenuCategoria,
        };
        const r = this.editingProducto
          ? await actualizarProducto(this.editingProducto.idMenuProducto, payload)
          : await crearProducto(payload);
        if (r.success) {
          await this.seleccionarCategoria(this.categoriaActiva);
          this.closeProductoModal();
          this.mostrarToast('success', '¡Éxito!', `Producto ${this.editingProducto ? 'actualizado' : 'agregado'}.`);
        } else {
          this.mostrarToast('error', 'Error', r.error || 'Error al guardar');
        }
      } finally { this.savingProducto = false; }
    },
    abrirModalEliminarProducto(p) {
      this.abrirModalConfirmacion({
        titulo: 'Eliminar Producto', subtitulo: 'Se quita del menú',
        mensaje: `¿Eliminar "${p.nombreProducto}" del menú?`,
        detalle: 'Se eliminará del menú pero el producto sigue en inventario.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600',
        botonTexto: 'Eliminar', datos: p.idMenuProducto,
        accion: (id) => this.deleteProducto(id),
      });
    },
    async deleteProducto(id) {
      const r = await eliminarProducto(id);
      if (r.success) {
        await this.seleccionarCategoria(this.categoriaActiva);
        this.mostrarToast('success', 'Eliminado', 'Producto eliminado del menú.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'No se pudo eliminar.');
      }
    },
    async toggleDisponibilidad(p) {
      const r = await cambiarDisponibilidadProducto(p.idMenuProducto, !p.disponible);
      if (r.success) {
        await this.seleccionarCategoria(this.categoriaActiva);
        this.mostrarToast('success', 'Actualizado', `Producto ${!p.disponible ? 'habilitado' : 'deshabilitado'}.`);
      }
    },

    // ===== CONFIRM MODAL =====
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-purple-600 to-pink-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-purple-600 to-pink-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    // Helper para templates
    formatearMonto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@media (max-width: 768px) { button { min-height: 44px; } input, select, textarea { font-size: 16px !important; } }
</style>